import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api, resolveMediaUrl } from "@/lib/api";
import { isEnabled, PAYMENTS_UI_ENABLED } from "@/lib/featureFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import ProfileCompletion from "@/components/ProfileCompletion";
import PhotoPicker from "@/components/PhotoPicker";
import IdentityMediaManager from "@/components/IdentityMediaManager";
import { TEST_IDS } from "@/constants/testIds";
import { Check, Loader2, Camera, Trash2, ExternalLink, Info, Sparkles, ThumbsUp, ThumbsDown, Bookmark, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { STYLE_GUIDE, STYLES } from "@/lib/styleGuide";

const BUDGETS = ["Budget","Mid Range","Premium","Luxury"];

export default function ProfileSettings() {
  const { user, refresh, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [completion, setCompletion] = useState(0);
  const [missing, setMissing] = useState([]);
  const missingSet = new Set(missing);
  const [usage, setUsage] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || "",
    username: user?.username || "",
    bio: user?.bio || "",
    age: user?.age || "",
    gender: user?.gender || "",
    height_cm: user?.height_cm || "",
    weight_kg: user?.weight_kg || "",
    city: user?.city || "",
    country: user?.country || "",
    occupation: user?.occupation || "",
    style_preferences: user?.style_preferences || [],
    budget_preference: user?.budget_preference || "",
    privacy: user?.privacy || "public",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPicture, setSavingPicture] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pollState, setPollState] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [portrait, setPortrait] = useState({ url: user?.identity_portrait_url || null, confirmed: user?.identity_confirmed ?? null, generating: false });

  const generatePortrait = async () => {
    setPortrait((p) => ({ ...p, generating: true }));
    try {
      const { data } = await api.post("/users/me/identity-portrait", {});
      setPortrait({ url: data.portrait_url, confirmed: null, generating: false });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : "Could not generate portrait");
      setPortrait((p) => ({ ...p, generating: false }));
    }
  };

  const confirmPortrait = async (accurate) => {
    try {
      await api.post("/users/me/identity-confirm", { accurate });
      setPortrait((p) => ({ ...p, confirmed: accurate }));
      if (accurate) {
        toast.success("Great — your AI identity is locked in.");
      } else {
        toast.info("Thanks. Add a clearer photo and re-run to improve the match.");
      }
    } catch { toast.error("Could not save"); }
  };

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f,
      name: user.name || "", username: user.username || "", bio: user.bio || "",
      age: user.age || "", gender: user.gender || "",
      height_cm: user.height_cm || "", weight_kg: user.weight_kg || "",
      city: user.city || "", country: user.country || "", occupation: user.occupation || "",
      style_preferences: user.style_preferences || [],
      budget_preference: user.budget_preference || "",
      privacy: user.privacy || "public",
    }));
    api.get("/users/me/completion").then(({ data }) => {
      setCompletion(data.percent);
      setMissing(data.missing || []);
    });
    api.get("/users/me/usage").then(({ data }) => setUsage(data));
  }, [user]);

  // Stripe checkout return polling
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (params.get("canceled") === "1") {
      toast.info("Checkout canceled");
      params.delete("canceled"); setParams(params, { replace: true });
      return;
    }
    if (!sessionId) return;
    let attempts = 0, cancelled = false, timer = null;
    setPollState({ status: "polling" });
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setPollState({ status: "paid" });
          toast.success("Subscription active");
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") {
          setPollState({ status: "expired" });
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (attempts < 8) timer = setTimeout(tick, 2500);
        else setPollState({ status: "timeout" });
      } catch { if (!cancelled) setPollState({ status: "error" }); }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUsername = async (u) => {
    if (!u || u === user?.username) { setUsernameStatus(null); return; }
    try {
      const { data } = await api.get(`/users/check-username?u=${encodeURIComponent(u)}`);
      setUsernameStatus(data);
    } catch { setUsernameStatus(null); }
  };

  const toggleStyle = (s) => setForm((f) => ({
    ...f,
    style_preferences: f.style_preferences.includes(s)
      ? f.style_preferences.filter((x) => x !== s)
      : [...f.style_preferences, s],
  }));

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
      };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      const { data } = await api.patch("/users/me", payload);
      setUser(data);
      const { data: c } = await api.get("/users/me/completion");
      setCompletion(c.percent);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSavingProfile(false); }
  };

  const uploadPicture = async (files) => {
    const file = Array.isArray(files) ? files[0] : files?.target?.files?.[0];
    if (!file) return;
    setSavingPicture(true);
    try {
      const fd = new FormData(); fd.append("image", file);
      await api.post("/users/me/picture", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("New picture uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setSavingPicture(false); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/users/me");
      toast.success("Account deleted");
      navigate("/login", { replace: true });
    } catch { toast.error("Could not delete"); }
  };

  const buy = async (plan_id) => {
    setLoadingPlan(plan_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const downgrade = async () => {
    if (!confirm("Move back to Free plan?")) return;
    await api.post("/subscription/upgrade", { tier: "free" });
    await refresh();
    toast.success("Switched to Free");
  };

  const activeTier = user?.subscription_tier || "free";
  const publicUrl = form.username ? `${window.location.origin}/@${form.username}` : null;
  const isOwner = true;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <p className="eyebrow">Profile</p>
      <h1 className="editorial-h1 mt-3">Your details.</h1>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        {/* Left card */}
        <div className="lg:col-span-4 border border-border p-6 lg:p-8">
          <div className="flex flex-col items-center">
            <PhotoPicker
              onSelect={uploadPicture}
              multiple={false}
              cameraFacing="user"
              testId="profile-picture-picker"
            >
              <button type="button" className="relative group" data-testid="profile-picture-trigger">
                {isOwner ? (
                  <ProfileCompletion picture={user?.picture} name={user?.name} percent={completion} size={140} showLabel />
                ) : (
                  <div className="h-32 w-32 rounded-full border border-border overflow-hidden">
                    {user?.picture && <img src={resolveMediaUrl(user.picture)} alt="" className="h-full w-full object-cover" />}
                  </div>
                )}
                <span className="absolute -bottom-1 right-1 bg-accent text-accent-foreground p-2 rounded-full opacity-0 group-hover:opacity-100 transition">
                  {savingPicture ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                </span>
              </button>
            </PhotoPicker>
            <p className="font-serif text-2xl mt-5 tracking-tight">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
            {publicUrl && (
              <a href={`/u/${form.username}`}
                 className="mt-3 text-xs uppercase tracking-[0.18em] underline underline-offset-4 flex items-center gap-1"
                 data-testid="public-profile-link">
                @{form.username} <ExternalLink size={10} />
              </a>
            )}
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <Link
              to="/collections"
              data-testid="profile-saved-link"
              className="group flex items-center justify-between gap-3 -mx-2 px-2 py-3 hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 inline-flex items-center justify-center bg-accent text-accent-foreground group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                  <Bookmark size={14} />
                </span>
                <div>
                  <p className="font-serif text-lg leading-none">Saved</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">Try-ons · Atelier · Outfits · Posts</p>
                </div>
              </div>
              <ChevronRight size={16} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <p className="eyebrow">Current plan</p>
            <p className="font-serif text-2xl mt-2 capitalize">{activeTier.replace("_", " ")}</p>
            {user?.subscription_expires_at && activeTier !== "free" && (
              <p className="text-xs text-muted-foreground mt-1">
                Renews {new Date(user.subscription_expires_at).toLocaleDateString()}
              </p>
            )}
            {usage && activeTier === "free" && (
              <div className="mt-4 space-y-1.5 text-[11px] text-muted-foreground">
                <p>· {usage.usage.outfits}/{usage.quotas.outfits} outfits today</p>
                <p>· {usage.usage.tryons}/{usage.quotas.tryons} try-ons today</p>
                <p>· {usage.usage.chat_messages}/{usage.quotas.chat_messages} stylist messages today</p>
              </div>
            )}
            {PAYMENTS_UI_ENABLED() && (
              <Button onClick={() => navigate("/plans")} variant="outline" data-testid="see-plans-btn"
                      className="mt-4 w-full rounded-none uppercase tracking-[0.18em] text-[10px]">Compare plans</Button>
            )}
          </div>

          <div className="mt-6 border-t border-border pt-6 space-y-2">
            <Button onClick={async () => { await logout(); navigate("/login"); }} variant="outline" className="rounded-none uppercase tracking-[0.2em] text-xs w-full">Sign out</Button>
            <Button onClick={() => setDeleteOpen(true)} data-testid="profile-delete-btn"
                    variant="outline"
                    className="rounded-none uppercase tracking-[0.2em] text-xs w-full text-destructive hover:bg-destructive hover:text-destructive-foreground">
              <Trash2 size={12} className="mr-2" /> Delete account
            </Button>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-8 space-y-6">
          {pollState?.status === "polling" && (
            <div className="border border-border p-4 flex items-center gap-3 bg-card">
              <Loader2 size={16} className="animate-spin" /><p className="text-sm">Confirming your payment…</p>
            </div>
          )}

          {/* Community profile (display name, username, bio, privacy) */}
          <section id="community" className="border border-border p-6 lg:p-8 scroll-mt-24">
            <p className="eyebrow">Community profile</p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Field label="Display name">
                <Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Username">
                <Input data-testid="profile-username-input" value={form.username}
                       onChange={(e) => { const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""); setForm({ ...form, username: v }); checkUsername(v); }}
                       className="rounded-none" />
                {usernameStatus && (
                  <p className={`text-[10px] mt-1 uppercase tracking-[0.18em] ${usernameStatus.available ? "text-accent" : "text-destructive"}`}>
                    {usernameStatus.available ? "Available ✓" : usernameStatus.reason}
                  </p>
                )}
              </Field>
              <div className="md:col-span-2">
                <Field label="Bio">
                  <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="One line about your style…" className="rounded-none min-h-[60px]" />
                </Field>
              </div>
              <Field label="Privacy">
                <Select value={form.privacy} onValueChange={(v) => setForm({ ...form, privacy: v })}>
                  <SelectTrigger className="rounded-none" data-testid="profile-privacy-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="followers_only">Followers only</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          {/* Basics — personal details */}
          <section id="personal" className="border border-border p-6 lg:p-8 scroll-mt-24">
            <p className="eyebrow">Personal details</p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Field label="Age" missing={missingSet.has("age")} hint="Helps personalize style age-range.">
                <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Gender" missing={missingSet.has("gender")} hint="Used to filter Curated picks.">
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="prefer-not">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Height (cm)" missing={missingSet.has("height_cm")} hint="Improves Try-On proportion accuracy.">
                <Input type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Weight (kg)" missing={missingSet.has("weight_kg")} hint="Helps sizing & fit recommendations.">
                <Input type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="City" missing={missingSet.has("city")} hint="Powers weather-aware outfits.">
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Country" missing={missingSet.has("country")} hint="Localizes retailers & sizes.">
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Occupation" missing={missingSet.has("occupation")} hint="Shapes workwear suggestions.">
                <Input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} className="rounded-none" />
              </Field>
            </div>
          </section>

          {/* Style — AI Identity & Style */}
          <section id="identity" className="border border-border p-6 lg:p-8 scroll-mt-24">
            <p className="eyebrow">AI Identity &amp; Style</p>
            <p className="text-xs text-muted-foreground mt-1">Tap the <Info size={11} className="inline -mt-0.5" /> icon to see a reference look for each style.</p>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              {STYLES.map((s) => {
                const on = form.style_preferences.includes(s);
                const ref = STYLE_GUIDE[s];
                return (
                  <div key={s} className={`border transition ${on ? "border-foreground" : "border-border hover:border-foreground"}`}>
                    <button onClick={() => toggleStyle(s)} className="block w-full text-left" data-testid={`style-${s}`}>
                      <div className="aspect-[5/3] overflow-hidden bg-muted relative">
                        <img src={ref.image} alt={s} className="h-full w-full object-cover" />
                        {on && <span className="absolute top-2 right-2 bg-accent text-accent-foreground px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">Selected</span>}
                      </div>
                    </button>
                    <div className="px-3 py-2 flex items-center justify-between">
                      <button onClick={() => toggleStyle(s)} className="text-xs uppercase tracking-[0.18em]">{s}</button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button data-testid={`style-info-${s}`} aria-label={`About ${s}`} className="p-1 hover:opacity-60">
                            <Info size={12} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="rounded-none w-72">
                          <p className="font-serif text-xl">{s}</p>
                          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{ref.description}</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="eyebrow mt-8">Budget</p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              {BUDGETS.map((b) => (
                <button key={b} onClick={() => setForm({ ...form, budget_preference: b })}
                        className={`p-4 border text-left transition ${
                          form.budget_preference === b ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
                        }`}>
                  <p className="text-xs uppercase tracking-[0.2em]">{b}</p>
                </button>
              ))}
            </div>
          </section>

          <div className="flex justify-end">
            <Button data-testid="profile-save-btn" onClick={saveProfile} disabled={savingProfile}
                    className="rounded-none uppercase tracking-[0.2em] text-xs h-12 px-8">
              {savingProfile ? <><Loader2 size={14} className="animate-spin mr-2" />Saving</> : <><Check size={14} className="mr-2" />Save profile</>}
            </Button>
          </div>

          {/* Identity media gallery — photos & videos that power AI Try-On */}
          <IdentityMediaManager />

          {/* AI Identity confirmation */}
          <section className="border border-border p-6 lg:p-8" data-testid="identity-section">
            <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> AI identity</p>
            <h3 className="font-serif text-2xl tracking-tight mt-2">How accurately does this represent you?</h3>
            <p className="text-xs text-muted-foreground mt-2 max-w-lg">
              We generate a stylised portrait from your photos and Style DNA. Confirming it improves every outfit and try-on suggestion afterwards.
            </p>
            <div className="mt-5 grid md:grid-cols-[200px_1fr] gap-6 items-start">
              <div className="aspect-[3/4] bg-muted overflow-hidden border border-border w-full">
                {portrait.generating ? (
                  <div className="h-full flex flex-col items-center justify-center gap-3">
                    <Loader2 size={24} className="animate-spin" />
                    <p className="eyebrow pulse-soft">Composing</p>
                  </div>
                ) : portrait.url ? (
                  <img src={resolveMediaUrl(portrait.url)} alt="AI portrait" data-testid="identity-portrait-img" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center px-3">
                    Generate to see your AI portrait
                  </div>
                )}
              </div>
              <div>
                {!portrait.url && (
                  <Button data-testid="identity-generate-btn" onClick={generatePortrait} disabled={portrait.generating}
                          className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                    <Sparkles size={14} className="mr-2" /> Generate my AI portrait
                  </Button>
                )}
                {portrait.url && portrait.confirmed === null && (
                  <div className="flex flex-col gap-3">
                    <p className="text-sm">Does this feel like you?</p>
                    <div className="flex gap-3">
                      <Button data-testid="identity-yes-btn" onClick={() => confirmPortrait(true)}
                              className="rounded-none uppercase tracking-[0.2em] text-[11px] h-11">
                        <ThumbsUp size={12} className="mr-2" /> Yes, accurate
                      </Button>
                      <Button data-testid="identity-no-btn" onClick={() => confirmPortrait(false)}
                              variant="outline" className="rounded-none uppercase tracking-[0.2em] text-[11px] h-11">
                        <ThumbsDown size={12} className="mr-2" /> Not quite
                      </Button>
                    </div>
                  </div>
                )}
                {portrait.url && portrait.confirmed === true && (
                  <div>
                    <p className="text-sm">Locked in. Stylebay is now tuned to your likeness.</p>
                    <Button onClick={generatePortrait} variant="outline"
                            data-testid="identity-regenerate-btn"
                            className="mt-3 rounded-none uppercase tracking-[0.18em] text-[10px] h-10">
                      Re-generate
                    </Button>
                  </div>
                )}
                {portrait.url && portrait.confirmed === false && (
                  <div className="space-y-3">
                    <p className="text-sm">Upload another face or body photo from your profile, then regenerate.</p>
                    <Button onClick={generatePortrait} disabled={portrait.generating}
                            data-testid="identity-regenerate-btn"
                            className="rounded-none uppercase tracking-[0.2em] text-[11px] h-11">
                      <Sparkles size={12} className="mr-2" /> Try again
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </section>

          {isEnabled("STRIPE_UI") && (
          <section className="border border-border p-6 lg:p-8">
            <p className="eyebrow">Subscription</p>
            <div className="mt-4 grid md:grid-cols-3 gap-3">
              <Button data-testid={TEST_IDS.profile.upgradeBtn} disabled={activeTier === "premium_monthly" || loadingPlan === "premium_monthly"}
                      onClick={() => buy("premium_monthly")}
                      className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                {activeTier === "premium_monthly" ? "Monthly · Active" : loadingPlan === "premium_monthly" ? <Loader2 size={14} className="animate-spin" /> : "Subscribe ₹299/mo"}
              </Button>
              <Button data-testid="upgrade-premium_annual" disabled={activeTier === "premium_annual" || loadingPlan === "premium_annual"}
                      onClick={() => buy("premium_annual")}
                      className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                {activeTier === "premium_annual" ? "Annual · Active" : loadingPlan === "premium_annual" ? <Loader2 size={14} className="animate-spin" /> : "Subscribe ₹2,999/yr"}
              </Button>
              {activeTier !== "free" && (
                <Button data-testid="downgrade-free-btn" variant="outline" onClick={downgrade}
                        className="rounded-none uppercase tracking-[0.2em] text-xs h-11">Switch to Free</Button>
              )}
            </div>
            <p className="mt-4 text-[11px] text-muted-foreground">
              Test card: <code className="px-1.5 py-0.5 border border-border">4242 4242 4242 4242</code> · Any future expiry · Any CVC
            </p>
          </section>
          )}
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-serif text-3xl tracking-tight">Delete account</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently removes your account, sessions, wardrobe and posts. This cannot be undone.
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-none uppercase tracking-[0.2em] text-xs">Cancel</Button>
            <Button onClick={deleteAccount} data-testid="confirm-delete-btn" className="rounded-none uppercase tracking-[0.2em] text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <Trash2 size={12} className="mr-1.5" /> Delete forever
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, missing, hint }) {
  return (
    <div
      className={`relative rounded-none transition-colors ${
        missing ? "border-l-2 border-rose-400 dark:border-rose-500 bg-rose-50/60 dark:bg-rose-950/20 pl-3 pr-2 py-2 -ml-3" : ""
      }`}
    >
      <Label className="eyebrow flex items-center gap-2">
        {label}
        {missing && (
          <span className="text-[9px] uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 font-medium">
            · missing
          </span>
        )}
      </Label>
      <div className="mt-1.5">{children}</div>
      {missing && hint && (
        <p className="text-[10px] text-rose-600 dark:text-rose-400 mt-1.5">{hint}</p>
      )}
    </div>
  );
}
